import React, { useState, useEffect } from 'react';
import { countriesService } from '../../../services/supabaseService';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const CountryFilter = ({ language, selectedCountry, onCountryChange, onProcessingChange, onElevationChange }) => {
  const [countries, setCountries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedProcessing, setSelectedProcessing] = useState('all');
  const [elevationRange, setElevationRange] = useState({ min: '', max: '' });

  const africanCountries = [
    { code: 'DZ', nameEn: 'Algeria', nameZh: '阿尔及利亚' },
    { code: 'AO', nameEn: 'Angola', nameZh: '安哥拉' },
    { code: 'BJ', nameEn: 'Benin', nameZh: '贝宁' },
    { code: 'BW', nameEn: 'Botswana', nameZh: '博茨瓦纳' },
    { code: 'BF', nameEn: 'Burkina Faso', nameZh: '布基纳法索' },
    { code: 'BI', nameEn: 'Burundi', nameZh: '布隆迪' },
    { code: 'CV', nameEn: 'Cabo Verde', nameZh: '佛得角' },
    { code: 'CM', nameEn: 'Cameroon', nameZh: '喀麦隆' },
    { code: 'CF', nameEn: 'Central African Republic', nameZh: '中非共和国' },
    { code: 'TD', nameEn: 'Chad', nameZh: '乍得' },
    { code: 'KM', nameEn: 'Comoros', nameZh: '科摩罗' },
    { code: 'CG', nameEn: 'Republic of the Congo', nameZh: '刚果（布）' },
    { code: 'CD', nameEn: 'Democratic Republic of the Congo', nameZh: '刚果（金）' },
    { code: 'CI', nameEn: "Côte d'Ivoire", nameZh: '科特迪瓦' },
    { code: 'DJ', nameEn: 'Djibouti', nameZh: '吉布提' },
    { code: 'EG', nameEn: 'Egypt', nameZh: '埃及' },
    { code: 'GQ', nameEn: 'Equatorial Guinea', nameZh: '赤道几内亚' },
    { code: 'ER', nameEn: 'Eritrea', nameZh: '厄立特里亚' },
    { code: 'SZ', nameEn: 'Eswatini', nameZh: '斯威士兰' },
    { code: 'ET', nameEn: 'Ethiopia', nameZh: '埃塞俄比亚' },
    { code: 'GA', nameEn: 'Gabon', nameZh: '加蓬' },
    { code: 'GM', nameEn: 'Gambia', nameZh: '冈比亚' },
    { code: 'GH', nameEn: 'Ghana', nameZh: '加纳' },
    { code: 'GN', nameEn: 'Guinea', nameZh: '几内亚' },
    { code: 'GW', nameEn: 'Guinea-Bissau', nameZh: '几内亚比绍' },
    { code: 'KE', nameEn: 'Kenya', nameZh: '肯尼亚' },
    { code: 'LS', nameEn: 'Lesotho', nameZh: '莱索托' },
    { code: 'LR', nameEn: 'Liberia', nameZh: '利比里亚' },
    { code: 'LY', nameEn: 'Libya', nameZh: '利比亚' },
    { code: 'MG', nameEn: 'Madagascar', nameZh: '马达加斯加' },
    { code: 'MW', nameEn: 'Malawi', nameZh: '马拉维' },
    { code: 'ML', nameEn: 'Mali', nameZh: '马里' },
    { code: 'MR', nameEn: 'Mauritania', nameZh: '毛里塔尼亚' },
    { code: 'MU', nameEn: 'Mauritius', nameZh: '毛里求斯' },
    { code: 'MA', nameEn: 'Morocco', nameZh: '摩洛哥' },
    { code: 'MZ', nameEn: 'Mozambique', nameZh: '莫桑比克' },
    { code: 'NA', nameEn: 'Namibia', nameZh: '纳米比亚' },
    { code: 'NE', nameEn: 'Niger', nameZh: '尼日尔' },
    { code: 'NG', nameEn: 'Nigeria', nameZh: '尼日利亚' },
    { code: 'RW', nameEn: 'Rwanda', nameZh: '卢旺达' },
    { code: 'ST', nameEn: 'São Tomé and Príncipe', nameZh: '圣多美和普林西比' },
    { code: 'SN', nameEn: 'Senegal', nameZh: '塞内加尔' },
    { code: 'SC', nameEn: 'Seychelles', nameZh: '塞舌尔' },
    { code: 'SL', nameEn: 'Sierra Leone', nameZh: '塞拉利昂' },
    { code: 'SO', nameEn: 'Somalia', nameZh: '索马里' },
    { code: 'ZA', nameEn: 'South Africa', nameZh: '南非' },
    { code: 'SS', nameEn: 'South Sudan', nameZh: '南苏丹' },
    { code: 'SD', nameEn: 'Sudan', nameZh: '苏丹' },
    { code: 'TZ', nameEn: 'Tanzania', nameZh: '坦桑尼亚' },
    { code: 'TG', nameEn: 'Togo', nameZh: '多哥' },
    { code: 'TN', nameEn: 'Tunisia', nameZh: '突尼斯' },
    { code: 'UG', nameEn: 'Uganda', nameZh: '乌干达' },
    { code: 'ZM', nameEn: 'Zambia', nameZh: '赞比亚' },
    { code: 'ZW', nameEn: 'Zimbabwe', nameZh: '津巴布韦' },
    { code: 'EH', nameEn: 'Western Sahara', nameZh: '西撒哈拉' },
  ];

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      const data = await countriesService?.getAll();

      const byCode = new Map();
      (data || [])?.forEach((country) => {
        const code = String(country?.code || '').trim().toUpperCase();
        if (!code) return;
        if (!byCode.has(code)) {
          byCode.set(code, country);
        }
      });

      const merged = africanCountries
        ?.filter((c) => c?.code)
        ?.reduce((acc, canonical) => {
          const code = String(canonical?.code)?.toUpperCase();
          if (acc?.some((x) => x?.code === code)) return acc;

          const fromDb = byCode.get(code);
          const nameEn = fromDb?.nameEn || canonical?.nameEn;
          const nameZh = fromDb?.nameZh || canonical?.nameZh || canonical?.nameEn;
          const flagUrl = fromDb?.flagUrl || `https://flagcdn.com/${code?.toLowerCase()}.svg`;

          acc.push({
            ...fromDb,
            id: fromDb?.id || code,
            code,
            nameEn,
            nameZh,
            flagUrl
          });

          return acc;
        }, []);

      setCountries(merged || []);
    } catch (error) {
      console.error('Failed to load countries:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCountries = countries?.filter((country) => {
    const name = language === 'en' ? country?.nameEn : country?.nameZh;
    if (!searchTerm) return true;
    return String(name || '')?.toLowerCase()?.includes(String(searchTerm || '')?.toLowerCase());
  });

  const processingMethods = [
    { value: 'all', labelEn: 'All Methods', labelZh: '所有方法' },
    { value: 'washed', labelEn: 'Washed', labelZh: '水洗' },
    { value: 'natural', labelEn: 'Natural', labelZh: '日晒' },
    { value: 'honey', labelEn: 'Honey', labelZh: '蜜处理' },
    { value: 'semi_washed', labelEn: 'Semi-Washed', labelZh: '半水洗' },
    { value: 'wet_hulled', labelEn: 'Wet Hulled', labelZh: '湿刨' }
  ];

  const handleProcessingChange = (value) => {
    setSelectedProcessing(value);
    onProcessingChange?.(value === 'all' ? null : value);
  };

  const handleElevationChange = (type, value) => {
    const newRange = { ...elevationRange, [type]: value };
    setElevationRange(newRange);
    onElevationChange?.(newRange);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Icon name="Loader2" size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block bg-card rounded-lg border border-border p-4 shadow-card sticky top-20">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Filter" size={20} />
          {language === 'en' ? 'Filters' : '筛选'}
        </h3>

        {/* Country Search */}
        <div className="mb-6">
          <label className="text-sm font-medium coffee-text-dark mb-2 block">
            {language === 'en' ? 'Search Country' : '搜索国家'}
          </label>
          <Input
            type="search"
            placeholder={language === 'en' ? 'Search African countries...' : '搜索非洲国家...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full"
          />
          {searchTerm && filteredCountries?.length > 0 && (
            <div className="mt-2 max-h-48 overflow-y-auto border border-border rounded-md bg-background">
              {filteredCountries?.map((country) => (
                <button
                  key={country?.code || country?.id}
                  onClick={() => {
                    onCountryChange(country?.id);
                    setSearchTerm('');
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted transition-smooth text-left text-sm"
                >
                  {country?.flagUrl ? (
                    <img
                      src={country?.flagUrl}
                      alt=""
                      className="w-5 h-4 rounded-sm border border-border object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <span className="text-base">🌍</span>
                  )}
                  <span>{language === 'en' ? country?.nameEn : country?.nameZh}</span>
                </button>
              ))}
            </div>
          )}
          {selectedCountry && selectedCountry !== 'all' && (
            <button
              onClick={() => onCountryChange('all')}
              className="mt-2 text-sm coffee-text-medium hover:underline flex items-center gap-1"
            >
              <Icon name="X" size={14} />
              {language === 'en' ? 'Clear country filter' : '清除国家筛选'}
            </button>
          )}
        </div>

        {/* Processing Method Filter */}
        <div className="mb-6">
          <label className="text-sm font-medium coffee-text-dark mb-2 block">
            {language === 'en' ? 'Processing Method' : '加工方法'}
          </label>
          <div className="space-y-2">
            {processingMethods?.map((method) => (
              <button
                key={method?.value}
                onClick={() => handleProcessingChange(method?.value)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md transition-smooth text-sm ${
                  selectedProcessing === method?.value
                    ? 'coffee-bg-medium shadow-sm'
                    : 'bg-muted hover:bg-muted/80 text-foreground'
                }`}
              >
                <Icon name="Coffee" size={16} />
                <span>{language === 'en' ? method?.labelEn : method?.labelZh}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Elevation Range Filter */}
        <div className="mb-4">
          <label className="text-sm font-medium coffee-text-dark mb-2 block">
            {language === 'en' ? 'Elevation (meters)' : '海拔（米）'}
          </label>
          <div className="space-y-2">
            <Input
              type="number"
              placeholder={language === 'en' ? 'Min elevation' : '最低海拔'}
              value={elevationRange?.min}
              onChange={(e) => handleElevationChange('min', e?.target?.value)}
              className="w-full"
            />
            <Input
              type="number"
              placeholder={language === 'en' ? 'Max elevation' : '最高海拔'}
              value={elevationRange?.max}
              onChange={(e) => handleElevationChange('max', e?.target?.value)}
              className="w-full"
            />
          </div>
          {(elevationRange?.min || elevationRange?.max) && (
            <button
              onClick={() => {
                setElevationRange({ min: '', max: '' });
                onElevationChange?.({ min: '', max: '' });
              }}
              className="mt-2 text-sm coffee-text-medium hover:underline flex items-center gap-1"
            >
              <Icon name="X" size={14} />
              {language === 'en' ? 'Clear elevation filter' : '清除海拔筛选'}
            </button>
          )}
        </div>
      </div>

      {/* Mobile Filters */}
      <div className="lg:hidden mb-6 space-y-4">
        {/* Country Search */}
        <div>
          <Input
            type="search"
            placeholder={language === 'en' ? 'Search African countries...' : '搜索非洲国家...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full"
          />
          {searchTerm && filteredCountries?.length > 0 && (
            <div className="mt-2 max-h-48 overflow-y-auto border border-border rounded-md bg-background">
              {filteredCountries?.map((country) => (
                <button
                  key={country?.code || country?.id}
                  onClick={() => {
                    onCountryChange(country?.id);
                    setSearchTerm('');
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted transition-smooth text-left text-sm"
                >
                  {country?.flagUrl ? (
                    <img
                      src={country?.flagUrl}
                      alt=""
                      className="w-5 h-4 rounded-sm border border-border object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <span className="text-base">🌍</span>
                  )}
                  <span>{language === 'en' ? country?.nameEn : country?.nameZh}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Processing Method - Horizontal Scroll */}
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-2 min-w-max">
            {processingMethods?.map((method) => (
              <button
                key={method?.value}
                onClick={() => handleProcessingChange(method?.value)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-smooth text-sm ${
                  selectedProcessing === method?.value
                    ? 'coffee-bg-medium shadow-md'
                    : 'bg-card border border-border text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="Coffee" size={14} />
                <span>{language === 'en' ? method?.labelEn : method?.labelZh}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Elevation Range */}
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number"
            placeholder={language === 'en' ? 'Min elevation' : '最低海拔'}
            value={elevationRange?.min}
            onChange={(e) => handleElevationChange('min', e?.target?.value)}
          />
          <Input
            type="number"
            placeholder={language === 'en' ? 'Max elevation' : '最高海拔'}
            value={elevationRange?.max}
            onChange={(e) => handleElevationChange('max', e?.target?.value)}
          />
        </div>
      </div>
    </>
  );
};

export default CountryFilter;